import { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowDown,
    ArrowUp,
    CircleAlert,
    Cloud,
    ExternalLink,
    History,
    Loader2,
    RotateCcw,
    Trash2
} from "@fg/icons";
import type { LargestFile, StorageCategoryKey, StorageSummary } from "@/api/driveApi";
import { useLargestFiles, usePruneVersions, useStorageSummary } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import type { FileCategory } from "@/lib/fileTypes";
import { formatBytes, formatShortDate, plural } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useDriveActions } from "../driveActions";
import { useDialogs } from "../dialogs/DialogsProvider";
import { useToast } from "../toast/ToastProvider";
import { PageTitle } from "../DriveView";
import { EmptyState } from "../EmptyState";
import { ItemIcon } from "../items/ItemIcon";

const CATEGORIES: Record<StorageCategoryKey, { label: string; bar: string; icon: FileCategory }> = {
    documents: { label: "Documents", bar: "bg-blue-600 dark:bg-blue-500", icon: "document" },
    spreadsheets: { label: "Spreadsheets", bar: "bg-emerald-600 dark:bg-emerald-500", icon: "spreadsheet" },
    presentations: { label: "Presentations", bar: "bg-orange-500 dark:bg-orange-400", icon: "presentation" },
    pdfs: { label: "PDFs", bar: "bg-red-600 dark:bg-red-500", icon: "pdf" },
    images: { label: "Images", bar: "bg-purple-600 dark:bg-purple-500", icon: "image" },
    videos: { label: "Videos", bar: "bg-pink-600 dark:bg-pink-500", icon: "video" },
    archives: { label: "Archives", bar: "bg-yellow-600 dark:bg-yellow-500", icon: "archive" },
    other: { label: "Other", bar: "bg-slate-400 dark:bg-slate-500", icon: "other" }
};
const VERSIONS_BAR = "bg-sky-300 dark:bg-sky-700";

/** /storage — what's using the quota, the bin and old versions, and the largest files. */
export function StoragePage() {
    const summary = useStorageSummary();
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-3 pt-3 sm:px-6">
                <div className="flex min-h-11 items-center">
                    <PageTitle icon={Cloud}>Storage</PageTitle>
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-3 pb-16 sm:px-6">
                <div className="mx-auto max-w-5xl space-y-6 pt-2">
                    {summary.isPending ? (
                        <div className="h-48 animate-pulse rounded-xl border bg-muted/40" aria-busy="true" aria-label="Loading storage" />
                    ) : summary.isError ? (
                        <EmptyState icon={CircleAlert} tone="rose" title="Couldn't load storage" description={summary.error.message}>
                            <Button variant="outline" onClick={() => void summary.refetch()}>
                                <RotateCcw className="h-4 w-4" /> Try again
                            </Button>
                        </EmptyState>
                    ) : (
                        <>
                            <UsageCard s={summary.data} />
                            <div className="grid gap-4 sm:grid-cols-2">
                                <VersionsCard s={summary.data} />
                                <BinCard s={summary.data} />
                            </div>
                        </>
                    )}
                    <LargestFiles />
                </div>
            </div>
        </div>
    );
}

function pctOf(bytes: number, quota: number): number {
    return quota > 0 ? Math.min(100, (bytes / quota) * 100) : 0;
}

function UsageCard({ s }: { s: StorageSummary }) {
    const pct = pctOf(s.usedBytes, s.quotaBytes);
    const segments = [
        ...s.categories.filter((c) => c.bytes > 0).map((c) => ({ key: c.key, label: CATEGORIES[c.key].label, bytes: c.bytes, bar: CATEGORIES[c.key].bar })),
        ...(s.versionBytes > 0 ? [{ key: "versions", label: "Old versions", bytes: s.versionBytes, bar: VERSIONS_BAR }] : [])
    ];
    const tone = pct >= 100 ? "text-destructive" : pct >= 85 ? "text-amber-600 dark:text-amber-400" : "text-foreground";
    return (
        <section aria-labelledby="usage-heading" className="rounded-xl border bg-card p-4 text-card-foreground sm:p-6">
            <h2 id="usage-heading" className="sr-only">Storage used</h2>
            <p className="text-2xl font-semibold sm:text-3xl">
                <span className={tone}>{formatBytes(s.usedBytes)}</span>
                <span className="text-base font-normal text-muted-foreground sm:text-lg"> of {formatBytes(s.quotaBytes)} used</span>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
                {Math.round(pct)}% · {formatBytes(s.availableBytes)} free · {plural(s.fileCount, "file")}
            </p>
            {s.poolBytes != null && s.poolUsedBytes != null && (
                <p className="mt-1 text-sm text-muted-foreground">
                    Shared by your organisation: {formatBytes(s.poolUsedBytes)} of {formatBytes(s.poolBytes)} used
                    {s.seats ? ` across ${plural(s.seats, "licensed user")}` : ""}
                    {s.userCapBytes != null ? ` · your limit is ${formatBytes(s.userCapBytes)}` : ""}
                </p>
            )}
            <div
                className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-muted"
                role="img"
                aria-label={`${Math.round(pct)}% of storage used. ${segments.map((g) => `${g.label} ${formatBytes(g.bytes)}`).join(", ")}.`}
            >
                {segments.map((g) => (
                    <div
                        key={g.key}
                        className={cn("h-full border-r border-card last:border-r-0", g.bar)}
                        style={{ width: `${pctOf(g.bytes, s.quotaBytes)}%` }}
                        title={`${g.label}: ${formatBytes(g.bytes)}`}
                    />
                ))}
            </div>
            <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {s.categories.map((c) => {
                    const meta = CATEGORIES[c.key];
                    return (
                        <li key={c.key} className="flex min-w-0 items-center gap-2.5">
                            <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", meta.bar)} aria-hidden />
                            <ItemIcon item={{ kind: "File", name: `x.${iconExt(meta.icon)}`, contentType: null }} className="h-4 w-4" />
                            <span className="min-w-0 flex-1 truncate">{meta.label}</span>
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                                {formatBytes(c.bytes)}
                                <span className="hidden sm:inline"> · {c.count}</span>
                            </span>
                        </li>
                    );
                })}
                <li className="flex min-w-0 items-center gap-2.5">
                    <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", VERSIONS_BAR)} aria-hidden />
                    <History className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">Old versions</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                        {formatBytes(s.versionBytes)}
                        <span className="hidden sm:inline"> · {s.versionCount}</span>
                    </span>
                </li>
            </ul>
        </section>
    );
}

/** A representative file name per type so ItemIcon picks the right icon/colour. */
function iconExt(cat: FileCategory): string {
    const map: Partial<Record<FileCategory, string>> = {
        document: "docx", spreadsheet: "xlsx", presentation: "pptx", pdf: "pdf",
        image: "png", video: "mp4", archive: "zip"
    };
    return map[cat] ?? "bin";
}

function VersionsCard({ s }: { s: StorageSummary }) {
    return (
        <section aria-labelledby="versions-heading" className="rounded-xl border bg-card p-4 text-card-foreground">
            <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-sky-600 dark:text-sky-400" aria-hidden />
                <h2 id="versions-heading" className="text-sm font-semibold">Old versions</h2>
            </div>
            <p className="mt-2 text-2xl font-semibold">{formatBytes(s.versionBytes)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
                {s.versionCount > 0
                    ? `${plural(s.versionCount, "older version")} of your files count toward your storage. Use “Delete old versions” on a file below to keep only its current version.`
                    : "No old versions — every file is just its current version."}
            </p>
        </section>
    );
}

function BinCard({ s }: { s: StorageSummary }) {
    const actions = useDriveActions();
    return (
        <section aria-labelledby="bin-heading" className="rounded-xl border bg-card p-4 text-card-foreground">
            <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-rose-500" aria-hidden />
                <h2 id="bin-heading" className="text-sm font-semibold">Bin</h2>
            </div>
            <p className="mt-2 text-2xl font-semibold">{formatBytes(s.binBytes)}</p>
            <p className="mt-1 text-sm text-muted-foreground">
                {s.binCount > 0
                    ? `${plural(s.binCount, "item")} — deleted forever after 30 days. The bin doesn't count toward your storage.`
                    : "The bin is empty."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
                <Link
                    to="/trash"
                    className="inline-flex h-8 items-center justify-center rounded-md border border-input px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    Open bin
                </Link>
                {s.binCount > 0 ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => void actions.emptyBin(s.binCount)}
                    >
                        Empty bin
                    </Button>
                ) : null}
            </div>
        </section>
    );
}

function LargestFiles() {
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const largest = useLargestFiles(order);
    const rows = largest.data?.pages.flatMap((p) => p.items) ?? [];
    const total = largest.data?.pages[0]?.total ?? 0;

    return (
        <section aria-labelledby="largest-heading" className="rounded-xl border bg-card text-card-foreground">
            <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
                <h2 id="largest-heading" className="text-sm font-semibold">{order === "desc" ? "Largest files" : "Smallest files"}</h2>
                {total > 0 ? <span className="text-xs text-muted-foreground">Showing {rows.length} of {total}</span> : null}
            </div>
            {largest.isPending ? (
                <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading files">
                    {Array.from({ length: 6 }, (_, i) => (
                        <div key={i} className="h-10 animate-pulse rounded bg-muted" />
                    ))}
                </div>
            ) : largest.isError ? (
                <div className="flex flex-col items-center gap-3 p-8 text-sm text-muted-foreground">
                    <p>Couldn't load files. {largest.error.message}</p>
                    <Button variant="outline" size="sm" onClick={() => void largest.refetch()}>
                        <RotateCcw className="h-4 w-4" /> Try again
                    </Button>
                </div>
            ) : rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">No files yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[20rem] text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                                <th scope="col" className="px-4 py-2 font-medium">Name</th>
                                <th scope="col" className="hidden px-2 py-2 font-medium md:table-cell">Location</th>
                                <th scope="col" className="hidden px-2 py-2 font-medium lg:table-cell">Modified</th>
                                <th scope="col" className="px-2 py-2 text-right font-medium" aria-sort={order === "desc" ? "descending" : "ascending"}>
                                    <button
                                        type="button"
                                        onClick={() => setOrder(order === "desc" ? "asc" : "desc")}
                                        className="ml-auto inline-flex items-center gap-1 rounded px-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        aria-label={`Size, sorted ${order === "desc" ? "largest first" : "smallest first"}. Change order`}
                                    >
                                        Size
                                        {order === "desc" ? <ArrowDown className="h-3.5 w-3.5" aria-hidden /> : <ArrowUp className="h-3.5 w-3.5" aria-hidden />}
                                    </button>
                                </th>
                                <th scope="col" className="px-2 py-2 pr-4 text-right font-medium">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <LargestRow key={r.item.id} row={r} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {largest.hasNextPage ? (
                <div className="border-t p-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        disabled={largest.isFetchingNextPage}
                        onClick={() => void largest.fetchNextPage()}
                    >
                        {largest.isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {largest.isFetchingNextPage ? "Loading…" : "Show more"}
                    </Button>
                </div>
            ) : null}
        </section>
    );
}

function LargestRow({ row }: { row: LargestFile }) {
    const { item } = row;
    const actions = useDriveActions();
    const { confirm } = useDialogs();
    const toast = useToast();
    const prune = usePruneVersions();
    const folderLink = `${item.parentId ? `/folder/${encodeURIComponent(item.parentId)}` : "/"}?select=${encodeURIComponent(item.id)}`;
    const hasOld = row.versionCount > 1;

    const deleteOldVersions = async () => {
        const ok = await confirm({
            title: "Delete old versions?",
            message: `Keeps only the current version of “${item.name}” and deletes ${plural(row.versionCount - 1, "older version")}, freeing about ${formatBytes(row.oldVersionBytes)}. You can't undo this.`,
            confirmLabel: "Delete old versions",
            destructive: true
        });
        if (!ok) return;
        try {
            const res = await prune.mutateAsync(item.id);
            toast.success(
                res.removedCount > 0
                    ? `Deleted ${plural(res.removedCount, "old version")} of “${item.name}” — freed ${formatBytes(res.freedBytes)}`
                    : `“${item.name}” has no old versions`
            );
        } catch (e) {
            toast.error(e);
        }
    };

    const iconBtn = "flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

    return (
        <tr className="border-b last:border-b-0 hover:bg-accent/40">
            <td className="max-w-0 px-4 py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                    <ItemIcon item={item} />
                    <div className="min-w-0">
                        <p className="truncate font-medium" title={item.name}>{item.name}</p>
                        <p className="truncate text-xs text-muted-foreground md:hidden">{row.location}</p>
                        {hasOld ? (
                            <p className="truncate text-xs text-muted-foreground">
                                + {formatBytes(row.oldVersionBytes)} in {plural(row.versionCount - 1, "old version")}
                            </p>
                        ) : null}
                    </div>
                </div>
            </td>
            <td className="hidden max-w-[16rem] px-2 py-2 md:table-cell">
                <Link to={folderLink} className="block truncate rounded text-muted-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title={row.location}>
                    {row.location}
                </Link>
            </td>
            <td className="hidden whitespace-nowrap px-2 py-2 text-muted-foreground lg:table-cell">{formatShortDate(item.updatedAt)}</td>
            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{formatBytes(item.sizeBytes)}</td>
            <td className="px-2 py-2 pr-4">
                <div className="flex justify-end gap-0.5">
                    <button type="button" className={iconBtn} onClick={() => actions.open(item, [item])} aria-label={`Open ${item.name}`} title="Open">
                        <ExternalLink className="h-4 w-4" />
                    </button>
                    {hasOld ? (
                        <button
                            type="button"
                            className={iconBtn}
                            disabled={prune.isPending}
                            onClick={() => void deleteOldVersions()}
                            aria-label={`Delete old versions of ${item.name}`}
                            title="Delete old versions"
                        >
                            {prune.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                        </button>
                    ) : null}
                    <button type="button" className={iconBtn} onClick={() => void actions.trash([item])} aria-label={`Move ${item.name} to bin`} title="Move to bin">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </td>
        </tr>
    );
}
